import React, {useMemo, useEffect, useState} from "react";
import "./pricePageNew.css";
import {Helmet} from "react-helmet-async";
import {Table, Tag, Collapse} from "antd";
import Aos from "aos";
import "aos/dist/aos.css";

import OrderModal from "../common/OrderModal";

// Bring both; use priceList when available, else adapt priceData images
import {priceData as dataPriceImages} from "../../Data";

const ABS_BASE = "https://www.artdesignyana.com";
const abs = (p = "") => (p?.startsWith("http") ? p : `${ABS_BASE}${p || ""}`);

function useMediaQuery(query) {
    const [m, setM] = React.useState(() => window.matchMedia(query).matches);
    React.useEffect(() => {
        const mq = window.matchMedia(query);
        const h = e => setM(e.matches);
        mq.addEventListener?.("change", h);
        return () => mq.removeEventListener?.("change", h);
    }, [query]);
    return m;
}

const PricePage = () => {
    useEffect(() => {
        Aos.init({duration: 1500});
    }, []);

    const [modalOpen, setModalOpen] = useState(false);
    const [selectedService, setSelectedService] = useState("");

    const isMobile = useMediaQuery("(max-width: 700px)");

    // Normalize data
    const rows = useMemo(() => {
        return (dataPriceImages || []).map((p, i) => ({
            key: i,
            title: p.title,
            from: p.from,
            eta: p.eta,
            img: p.img,
            features: p.features || [],
            popular: !!p.popular,
        }));
    }, []);

    // Columns
    const columns = [
        {
            title: "Service",
            dataIndex: "title",
            key: "title",
            render: (text, record) => (
                <div className="svc">
                    <span className="svc_title">{text}</span>
                    {record.popular && <Tag color="gold" className="svc_badge">Popular</Tag>}
                </div>
            )
        },
        {
            title: "From",
            dataIndex: "from",
            key: "from",
            // responsive: ["sm"],
            render: v => v ? <span className="price_value">{v}</span> : "—"
        },
        {
            title: "",
            key: "action",
            render: (_, record) => (
                <button className="btn-table" onClick={() => {
                    setSelectedService(record.title);
                    setModalOpen(true);
                }}>
                    Order
                </button>
            )
        }
    ];

    const mobileItems = rows.map(r => ({
        key: r.key,
        label: (
            <div className="mrow_head">
                <div className="mrow_title">
                    <span className="svc_title">{r.title}</span>
                    {r.popular && <Tag color="gold" className="svc_badge">Popular</Tag>}
                </div>
                <div className="mrow_meta">
                    {r.from && <span className="mrow_price">{r.from}</span>}
                </div>
            </div>
        ),
        children: (
            <div className="mrow_body">
                {r.img && (
                    <div className="exp_img">
                        <img src={r.img} alt={r.title} loading="lazy" decoding="async"/>
                    </div>
                )}
                {r.features?.length > 0 && (
                    <ul className="exp_features">{r.features.map((f, i) => <li key={i}>{f}</li>)}</ul>
                )}
                <button
                    className="btn-table"
                    onClick={() => {
                        setSelectedService(r.title);
                        setModalOpen(true);
                    }}
                >
                    Order
                </button>
            </div>
        ),
    }));

    // Expanded panel per row
    const expandedRowRender = (record) => (
        <div className="row_expand">
            {record.img && (
                <div className="exp_img">
                    <img src={record.img} alt={record.title} loading="lazy" decoding="async"/>
                </div>
            )}
            {(record.features?.length > 0) && (
                <ul className="exp_features">
                    {record.features.map((f, i) => <li key={i}>{f}</li>)}
                </ul>
            )}
        </div>
    );

    // JSON-LD (OfferCatalog)
    const jsonLd = {
        "@context": "https://schema.org",
        "@type": "OfferCatalog",
        "name": "Order Sketches & Plans — Price List",
        "itemListElement": rows.map((r, i) => ({
            "@type": "Offer",
            "position": i + 1,
            "itemOffered": {"@type": "Service", "name": r.title},
            ...(r.from ? {
                "priceSpecification": {
                    "@type": "PriceSpecification",
                    "price": String(r.from).replace(/[^\d.]/g, ""),
                    "priceCurrency": "EUR"
                }
            } : {}),
            "url": abs("/sketch/price")
        }))
    };

    return (
        <section id="price_page">
            <Helmet>
                <title>Price List — Architectural sketches & 2D/3D plans</title>
                <meta name="description"
                      content="Transparent starting prices for interior/exterior sketches, 2D & 3D floor plans, architectural and landscape visuals."/>
                <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
            </Helmet>

            <div className="container price_table_wrap" data-aos="fade-up" data-aos-duration="2000">
                <div className="price_header">
                    <p className="eyebrow">Services & pricing</p>
                    <h1>Price List</h1>
                    <p className="price_sub">Clear starting prices for design sketches and visualisation. Final cost depends on project scope, detail level and timeline.</p>
                </div>

                {isMobile ? (
                    <Collapse accordion items={mobileItems} className="price_collapse"/>
                ) : (
                    <Table
                        className="price_table"
                        columns={columns}
                        dataSource={rows}
                        expandable={{expandedRowRender}}
                        pagination={false}
                        rowClassName="price_row"
                    />
                )}

                <aside className="price_note">
                    <strong>Save 10%</strong> when you order 3+ sketches.
                </aside>
            </div>
            <OrderModal
                open={modalOpen}
                onClose={() => setModalOpen(false)}
                service={selectedService}
            />
        </section>
    );
};
export default PricePage;
